import { useState } from 'react'
import { ServerApiClient } from '../../data/ServerApiClient'
import { computeTheirNumbers, parseDsoFact, parseTheirNumbers } from './theirNumbers'

/**
 * Three inputs the prospect knows cold, run through the same arithmetic the
 * product runs on the sample company, shown side by side. No generation, no
 * invented numbers — everything follows from what they typed. Results render
 * in place; the step's own Next button carries the tour forward.
 */
export function TourTheirNumbers({ facts, submitLabel, onCompute }: {
  facts: Record<string, string>
  submitLabel?: string | null
  onCompute: (props: Record<string, number | string | null>) => void
}) {
  // The ask rides INSIDE the result rather than becoming another step. This is
  // the one moment the visitor is looking at their own money, and a step
  // inserted here would land at 9 of 11 — late, where the evidence rewards the
  // middle. Optional in both directions: the tour continues either way.
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const sendBreakdown = async () => {
    const trimmed = email.trim()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) { setSent('error'); return }
    setSent('sending')
    try {
      await new ServerApiClient().submitDemoLead({ email: trimmed, source: 'tour' })
      setSent('sent')
    } catch {
      setSent('error')
    }
  }
  const [revenue, setRevenue] = useState('')
  const [headcount, setHeadcount] = useState('')
  const [days, setDays] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ReturnType<typeof computeTheirNumbers> | null>(null)

  const sampleDso = parseDsoFact(facts.dso_days)
  const sampleName = facts.company_name || 'the sample company'

  const compute = () => {
    const input = parseTheirNumbers({ revenue, headcount, days })
    if (!input) { setError('Three positive numbers — roughly is fine.'); return }
    setError(null)
    const out = computeTheirNumbers(input, sampleDso)
    setResult(out)
    onCompute({
      revenue: input.annualRevenue,
      headcount: input.headcount,
      collection_days: input.collectionDays,
      revenue_per_head: out.revenuePerHead,
      cash_in_receivables: out.cashInReceivables,
      dso_gap_days: out.dsoGapDays,
    })
  }

  return (
    <div className="fg-tour__theirnums">
      <div className="fg-tour__theirnums-grid">
        <label>
          Annual revenue
          <input inputMode="decimal" placeholder="$2,400,000" value={revenue}
            onChange={(e) => setRevenue(e.target.value)} />
        </label>
        <label>
          Headcount
          <input inputMode="numeric" placeholder="14" value={headcount}
            onChange={(e) => setHeadcount(e.target.value)} />
        </label>
        <label>
          Days customers take to pay
          <input inputMode="numeric" placeholder="45" value={days}
            onChange={(e) => setDays(e.target.value)} />
        </label>
      </div>
      {error ? <p className="fg-tour__theirnums-error" role="alert">{error}</p> : null}
      <button type="button" className="fg-tour__btn fg-tour__btn--primary" onClick={compute}>
        {submitLabel || 'Run my numbers'}
      </button>
      {result ? (
        <>
          <dl className="fg-tour__theirnums-results">
            <div><dt>Revenue per head</dt><dd>{result.revenuePerHead}</dd></div>
            <div><dt>Cash sitting in receivables</dt><dd>{result.cashInReceivables}</dd></div>
            <div><dt>Collecting 5 days faster frees</dt><dd>{result.fiveDaysFaster}</dd></div>
          </dl>
          {result.dsoGapDays != null ? (
            <p className="fg-tour__theirnums-compare">
              {result.dsoGapDays > 0
                ? `You collect ${result.dsoGapDays} days slower than ${sampleName} — that gap is cash on this screen.`
                : result.dsoGapDays < 0
                  ? `You collect ${Math.abs(result.dsoGapDays)} days faster than ${sampleName}. The rest of these numbers still compound.`
                  : `You collect on the same cycle as ${sampleName}.`}
            </p>
          ) : null}
          <div className="fg-tour__theirnums-ask">
            {sent === 'sent' ? (
              <p className="fg-tour__theirnums-sent">Sent — check your inbox. The tour keeps going.</p>
            ) : (
              <>
                <label htmlFor="fg-tn-email">Want this as a one-page breakdown?</label>
                <div className="fg-tour__theirnums-askrow">
                  <input
                    id="fg-tn-email"
                    type="email"
                    inputMode="email"
                    placeholder="you@yourfirm.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (sent === 'error') setSent('idle') }}
                  />
                  <button type="button" className="fg-tour__btn fg-tour__btn--ghost"
                    onClick={() => void sendBreakdown()} disabled={sent === 'sending'}>
                    {sent === 'sending' ? 'Sending…' : 'Email it to me'}
                  </button>
                </div>
                <p className="fg-tour__theirnums-fine">
                  {sent === 'error'
                    ? 'That address did not look right — check it and try again.'
                    : 'Optional. The tour keeps going either way.'}
                </p>
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}
